import { TOAST_DEFAULTS } from "@lib/config/toastConfig";
import {
  type AddToastInput,
  type ToastAction,
  type ToastType,
  useToastStore,
} from "@lib/stores/toast";
import { useMemo } from "react";

interface ToastOptions {
  message?: string | ((count: number) => string);
  duration?: number | "infinite";
  dedupeKey?: string;
  actions?: ToastAction[];
  position?:
    | "top-left"
    | "top-right"
    | "top-center"
    | "bottom-left"
    | "bottom-right"
    | "bottom-center";
}

interface PromiseToastOptions<T> {
  loading: string;
  success: string | ((data: T) => string);
  error: string | ((err: unknown) => string);
  duration?: number;
  /** Morph the loading toast to this type on success (default "success"). */
  successType?: "success" | "undo";
  /** Actions attached to the success toast (e.g. an "Annuler" undo button). */
  successActions?: ToastAction[];
}

interface ToastFn {
  (title: string, options?: ToastOptions): string;
  success: (title: string, options?: ToastOptions) => string;
  error: (title: string, options?: ToastOptions) => string;
  info: (title: string, options?: ToastOptions) => string;
  warning: (title: string, options?: ToastOptions) => string;
  undo: (title: string, options?: ToastOptions) => string;
  blocked: (title: string, options?: ToastOptions) => string;
  queued: (title: string, options?: ToastOptions) => string;
  promise: <T>(fn: () => Promise<T>, opts: PromiseToastOptions<T>) => Promise<T>;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

function makeToastFn(
  addToast: (input: AddToastInput) => string,
  morphToast: (id: string, patch: Record<string, unknown>, duration?: number) => void,
  removeToast: (id: string) => void,
  dismissAll: () => void,
): ToastFn {
  const create =
    (type: ToastType) =>
    (title: string, options?: ToastOptions): string =>
      addToast({ title, type, ...options });

  const toastFn = create("info") as ToastFn;

  toastFn.success = create("success");
  toastFn.error = create("error");
  toastFn.info = create("info");
  toastFn.warning = create("warning");
  toastFn.undo = create("undo");
  toastFn.blocked = create("blocked");
  toastFn.queued = create("queued");

  toastFn.promise = async (fn, opts) => {
    const id = addToast({ type: "loading", title: opts.loading, duration: "infinite" });
    try {
      const data = await fn();
      const successTitle = typeof opts.success === "function" ? opts.success(data) : opts.success;
      morphToast(
        id,
        {
          type: opts.successType ?? "success",
          title: successTitle,
          ...(opts.successActions ? { actions: opts.successActions } : {}),
        },
        opts.duration ?? TOAST_DEFAULTS.duration,
      );
      return data;
    } catch (err) {
      const errorTitle = typeof opts.error === "function" ? opts.error(err) : opts.error;
      morphToast(
        id,
        { type: "error", title: errorTitle },
        opts.duration ?? TOAST_DEFAULTS.errorDuration,
      );
      throw err;
    }
  };

  toastFn.dismiss = removeToast;
  toastFn.dismissAll = dismissAll;

  return toastFn;
}

export function useToast(): ToastFn {
  const addToast = useToastStore((s) => s.addToast);
  const morphToast = useToastStore((s) => s.morphToast);
  const removeToast = useToastStore((s) => s.removeToast);
  const dismissAll = useToastStore((s) => s.dismissAll);

  return useMemo(
    () => makeToastFn(addToast, morphToast, removeToast, dismissAll),
    [addToast, morphToast, removeToast, dismissAll],
  );
}

export default useToast;
export type { PromiseToastOptions, ToastFn, ToastOptions };
